import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { setSelectedFilesContentExpanded } from '@/chat-common/store/chat-ux.slice'
import { SourceSelectionDialog } from './SourceSelectionDialog'
import { AddFilesModal, type AddFilesModalInitialTab } from './AddFilesModal'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import {
  selectAgentConversationFormFilePaths,
  selectAgentConversationFormTemplateFilePaths,
  selectAgentConversationFormTemplateModeActive,
  selectAgentConversationFormUserFileIds,
  selectAgentConversationFormWritingSampleFileIds,
} from '@/agent/chat_form/store/selectors'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { RootState } from '@/store/store'

export type AssistantDocumentSelectionDialogDriveMode = 'legacy' | 'new'

type AssistantDocumentSelectionDialogProps = {
  open: boolean
  onClose: (value: boolean) => void
  conversationId: string
  isTemplateMode?: boolean
  driveMode: AssistantDocumentSelectionDialogDriveMode
  initialAddFilesTab?: AddFilesModalInitialTab
}

/**
 * AssistantDocumentSelectionDialog component.
 *
 * This component is used exclusively in the Assistant features
 * to allow the user to select documents for the Assistant to use by wrapping the SourceSelectionDialog component with the appropriate dispatch functions.
 *
 * @param props - The properties for the AssistantDocumentSelectionDialog component.
 * @returns A JSX element representing the document selection dialog.
 */
export default function AssistantDocumentSelectionDialog(props: AssistantDocumentSelectionDialogProps) {
  const { open, onClose, conversationId } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const prevOpenRef = useRef<boolean>(open)
  const selectionMadeRef = useRef<boolean>(false)
  const driveMode = props.driveMode

  // Get the global template mode state, but prioritize the prop if provided for backward compatibility
  const globalTemplateMode = useAppSelector(selectAgentConversationFormTemplateModeActive)
  const isTemplateModeActive = props.isTemplateMode !== undefined ? props.isTemplateMode : globalTemplateMode
  const selectedLegacyFilePaths = useAppSelector((state: RootState) => selectAgentConversationFormFilePaths(state, { conversationId }))
  const selectedTemplateFilePaths = useAppSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId }))
  const conversationMatterId = useAppSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId }))
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const effectiveMatterId = conversationMatterId ?? activeMatterId
  const selectedUserFileIds = useAppSelector((state: RootState) => selectAgentConversationFormUserFileIds(state, { conversationId }))
  const selectedWritingSampleFileIds = useAppSelector((state: RootState) => selectAgentConversationFormWritingSampleFileIds(state, { conversationId }))

  // analytics: drive dialog opened in template mode
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      selectionMadeRef.current = false
      if (isTemplateModeActive) {
        trackEvent(AnalyticsEvent.TemplateDriveDialogOpened, { conversationId })
      }
    }
    // dialog closed
    if (prevOpenRef.current && !open && isTemplateModeActive && !selectionMadeRef.current) {
      trackEvent(AnalyticsEvent.TemplateSourceDialogDismissed, { conversationId })
    }
    prevOpenRef.current = open
  }, [open, isTemplateModeActive, conversationId, trackEvent])

  const clearFiles = () => {
    dispatch(
      AgentConversationFormActions.clearFiles({
        conversationId: conversationId,
      })
    )
  }

  const addLegacyFileSelection = (file: string) => {
    dispatch(setSelectedFilesContentExpanded({ conversationId: conversationId, expanded: true }))
    dispatch(AgentConversationFormActions.addFile({ conversationId: conversationId, filePath: file }))
  }

  const removeLegacyFileSelection = (file: string) => {
    dispatch(setSelectedFilesContentExpanded({ conversationId: conversationId, expanded: true }))
    dispatch(AgentConversationFormActions.removeFile({ conversationId: conversationId, filePath: file }))
  }

  if (!isTemplateModeActive) {
    if (driveMode === 'legacy') {
      return (
        <SourceSelectionDialog
          conversationId={conversationId}
          open={open}
          onClose={onClose}
          selectedFilesOverride={selectedLegacyFilePaths}
          dispatchFunctions={{
            addFileSelection: addLegacyFileSelection,
            clearFilesSelection: clearFiles,
            toggleFileSelection: removeLegacyFileSelection,
          }}
        />
      )
    }
    return effectiveMatterId ? (
      <AddFilesModal
        open={open}
        onClose={() => onClose(false)}
        matterId={effectiveMatterId}
        initialSelectedFileIds={selectedUserFileIds}
        initialTab={props.initialAddFilesTab}
        onFilesSelected={(userFileIds) => {
          dispatch(AgentConversationFormActions.setUserFiles({ conversationId, userFileIds }))
          dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
        }}
      />
    ) : null
  }

  // New files UX: writing samples are chosen from My Drive (single-select user_files).
  if (driveMode === 'new') {
    return effectiveMatterId ? (
      <AddFilesModal
        open={open}
        onClose={() => onClose(false)}
        matterId={effectiveMatterId}
        title="Add writing sample"
        selectionMode="single"
        initialSelectedFileIds={selectedWritingSampleFileIds}
        initialTab={props.initialAddFilesTab}
        onFilesSelected={([userFileId]) => {
          // Single-select: a chosen file sets the sample; confirming with nothing selected (e.g.
          // after deselecting it) clears the existing sample.
          selectionMadeRef.current = true
          if (userFileId) {
            dispatch(AgentConversationFormActions.addWritingSampleFile({ conversationId, userFileId }))
          } else {
            dispatch(AgentConversationFormActions.clearWritingSampleFiles({ conversationId }))
          }
          dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
        }}
      />
    ) : null
  }

  const clearTemplateFiles = () => {
    dispatch(
      AgentConversationFormActions.clearTemplateFiles({
        conversationId: conversationId,
      })
    )
  }

  const addFileSelection = (file: string) => {
    dispatch(setSelectedFilesContentExpanded({ conversationId: conversationId, expanded: true }))
    dispatch(AgentConversationFormActions.addTemplateFile({ conversationId: conversationId, filePath: file }))
    selectionMadeRef.current = true
  }

  const removeFileSelection = (_file: string) => {
    dispatch(setSelectedFilesContentExpanded({ conversationId: conversationId, expanded: true }))
    // In template mode, we enforce a single selection and clear all for simplicity.
    clearTemplateFiles()
  }

  return (
    <SourceSelectionDialog
      conversationId={conversationId}
      open={open}
      onClose={onClose}
      allowMultipleSelection={false}
      selectedFilesOverride={selectedTemplateFilePaths}
      dispatchFunctions={{
        addFileSelection,
        clearFilesSelection: clearTemplateFiles,
        toggleFileSelection: removeFileSelection,
      }}
    />
  )
}
